import { pontosDaFormacao } from "../EscalacaoNoCampo";
import type { Jogada, LadoDaPartida, LanceDaPartida, PartidaView } from "./tipos";

/**
 * A coreografia do campo: de lance do motor a roteiro de ações.
 *
 * O motor não simula posição de ninguém. Ele decide, a cada trecho de cinco
 * minutos, quem ficou com a bola, se houve chance, que jogada foi, quem chutou,
 * quem deu o passe, e no que deu. Daqui sai um roteiro: quem recolhe a bola,
 * quem conduz, quem passa para quem, quem desarma, quem chuta e para onde. A
 * bola nunca anda sozinha: ou está no pé de alguém, ou está a caminho de
 * alguém, ou está morta esperando alguém a buscar. Quem executa o roteiro,
 * com velocidade de jogador e de bola, é o campo (`CampoAoVivo`).
 *
 * Nada aqui sorteia com o relógio: o pouco de variação (por qual lado sai a
 * bola, por qual ponta vem o cruzamento) sai de um hash do minuto e da ordem
 * do lance, então a mesma partida desenha o mesmo jogo duas vezes.
 */

export const CAMPO = { largura: 105, altura: 68 } as const;
const MEIO = { x: CAMPO.largura / 2, y: CAMPO.altura / 2 };

export interface Ponto {
  x: number;
  y: number;
}

export type Lado = "home" | "away";

export const outroLado = (lado: Lado): Lado => (lado === "home" ? "away" : "home");

export function segundoTempo(minuto: number): boolean {
  return minuto > 45;
}

/** O mandante ataca para a direita no primeiro tempo; os lados trocam no intervalo. */
export function ataqueParaDireita(lado: Lado, minuto: number): boolean {
  return (lado === "home") !== segundoTempo(minuto);
}

export function espelhar(p: Ponto): Ponto {
  return { x: CAMPO.largura - p.x, y: CAMPO.altura - p.y };
}

const limitar = (v: number, de: number, ate: number): number => Math.min(ate, Math.max(de, v));

/** Um ponto desenhado para "atacando para a direita", virado para o lado certo. */
export function orientar(p: Ponto, paraDireita: boolean): Ponto {
  return paraDireita ? p : espelhar(p);
}

/**
 * As posições base das vagas de um lado, atacando para a direita.
 *
 * É o mesmo desenho da tela de tática (`pontosDaFormacao`, ou os pontos da
 * formação personalizada), deitado: a profundidade vira o eixo longo do campo
 * e a largura vira o eixo curto. O bloco ocupa do próprio gol até um pouco
 * além do meio; quem tem a bola sobe, quem não tem recua, e isso o campo faz.
 */
export function basesDoLado(lado: Pick<LadoDaPartida, "formation" | "slots" | "custom">): Ponto[] {
  const pontos = lado.custom ?? pontosDaFormacao(lado.formation, lado.slots);
  return lado.slots.map((pos, i) => {
    if (pos === "GOL") return { x: 3.5, y: MEIO.y };
    const p = pontos[i] ?? { x: 0.5, y: 0.5 };
    const v = limitar(p.y, 0.17, 0.9);
    const x = 4 + ((0.9 - v) / (0.9 - 0.17)) * 68;
    const y = (1 - limitar(p.x, 0.06, 0.94)) * CAMPO.altura;
    return { x: limitar(x, 3, 80), y: limitar(y, 4, 64) };
  });
}

// ---------------------------------------------------------------------------
// O roteiro
// ---------------------------------------------------------------------------

export type Marca = "amarelo" | "vermelho" | "lesao" | "gol" | "entra" | "falta" | "impedimento";

export type Desfecho = "gol" | "defesa" | "fora";

export type Acao =
  /** Vai buscar a bola morta (ou a que está no chão) e fica com ela. */
  | { tipo: "recolhe"; quem: string }
  /** Vai até quem tem a bola e toma. */
  | { tipo: "desarme"; quem: string }
  /** Passe de um para outro. Quem recebe vai ao encontro; a bola chega nele. */
  | { tipo: "passe"; de: string; para: string; alto: boolean }
  /** Quem tem a bola conduz: até um ponto do campo (já no lado certo), ou tantos metros à frente. */
  | { tipo: "conduz"; quem: string; ate: Ponto | null; avanco: number; lateral: number }
  /** Alguém sem a bola corre para um ponto (já no lado certo), ou para a bola. */
  | { tipo: "corre"; quem: string; ate: Ponto | "bola" }
  /** Alguém já aparece num ponto: a saída de jogo, que se arma fora da tela. */
  | { tipo: "posiciona"; quem: string; em: Ponto }
  /** O juiz põe a bola num ponto (falta, pênalti): morta, esperando quem vai cobrar. */
  | { tipo: "coloca"; em: Ponto }
  /** Falta: `quem` vai até `sobre`, que tem a bola, e derruba. A bola morre ali. */
  | { tipo: "falta"; quem: string; sobre: string }
  /** Impedimento: a bandeira sobe quando a bola chega em `quem`. A bola morre ali. */
  | { tipo: "impedimento"; quem: string }
  /** Chute: a bola vai do pé ao alvo, e o desfecho é o que o motor disse. */
  | { tipo: "chute"; quem: string; alvo: Ponto; desfecho: Desfecho; goleiro: string | null; alto: boolean }
  | { tipo: "espera"; s: number }
  | { tipo: "marca"; quem: string; marca: Marca }
  | { tipo: "sai"; quem: string }
  | { tipo: "troca"; sai: string; entra: string }
  | { tipo: "intervalo" }
  /** Bola morta no meio, para a saída de jogo. */
  | { tipo: "reinicio" }
  /** O lance chegou a este ponto do roteiro: a lista e o som reagem agora. */
  | { tipo: "lance"; lance: LanceDaPartida };

/** O que o roteiro lembra de um trecho para o outro. */
export interface EstadoDoRoteiro {
  /** Quem termina o trecho com a bola, ou nulo se ela está morta. */
  dono: string | null;
  /** Onde a bola fica, no campo real, quando o trecho termina. */
  bola: Ponto;
  segundoTempo: boolean;
  /**
   * A próxima posse é uma saída de jogo armada: no começo da partida os dois
   * do círculo central já aparecem lá. Depois de um gol e na volta do
   * intervalo, não: o campo já os deixou perto do círculo, e eles andam os
   * últimos metros até a bola.
   */
  saidaArmada: boolean;
}

export function estadoInicial(): EstadoDoRoteiro {
  return { dono: null, bola: { ...MEIO }, segundoTempo: false, saidaArmada: true };
}

/** Um número estável em [0, 1) para um lance: a variação sem relógio. */
function hash(lance: LanceDaPartida, sal: number): number {
  let h = 2166136261 ^ (lance.minute * 97 + lance.ordem * 31 + sal * 7);
  const texto = `${lance.key}|${lance.playerId ?? ""}|${sal}`;
  for (let i = 0; i < texto.length; i += 1) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

const sinal = (v: number): number => (v < 0.5 ? -1 : 1);

/** As linhas de um lado: goleiro, defesa, meio, ataque, pelas vagas da formação. */
const LINHA_DA_POSICAO: Record<string, number> = {
  GOL: 0,
  ZAG: 1,
  LD: 1,
  LE: 1,
  VOL: 2,
  MC: 2,
  MEI: 2,
  MD: 2,
  ME: 2,
  PD: 3,
  PE: 3,
  ATA: 3,
};

interface Elenco {
  lado: Lado;
  /** Onde cada vaga fica, atacando para a direita. */
  bases: Ponto[];
  /** O jogador em cada vaga, ou nulo se a vaga está vazia. */
  ids: Array<string | null>;
  slots: string[];
}

function elencoDe(lado: Lado, view: LadoDaPartida): Elenco {
  return {
    lado,
    bases: basesDoLado(view),
    ids: view.emCampo.map((vaga) => (vaga.sentOff ? null : vaga.jogador.id)),
    slots: view.slots,
  };
}

function vagaDe(elenco: Elenco, id: string | null): number {
  return id === null ? -1 : elenco.ids.indexOf(id);
}

function linhaDe(elenco: Elenco, id: string | null): number {
  const vaga = vagaDe(elenco, id);
  if (vaga < 0) return 2;
  return LINHA_DA_POSICAO[elenco.slots[vaga] ?? ""] ?? 2;
}

function daLinha(elenco: Elenco, linha: number, exceto: string[] = []): string[] {
  const ids: string[] = [];
  elenco.ids.forEach((id, i) => {
    if (id === null || exceto.includes(id)) return;
    if ((LINHA_DA_POSICAO[elenco.slots[i] ?? ""] ?? 2) === linha) ids.push(id);
  });
  return ids;
}

function goleiroDe(elenco: Elenco): string | null {
  return daLinha(elenco, 0)[0] ?? null;
}

/** Base de um jogador, atacando para a direita. */
function baseDe(elenco: Elenco, id: string | null): Ponto {
  const vaga = vagaDe(elenco, id);
  return vaga >= 0 ? (elenco.bases[vaga] ?? MEIO) : MEIO;
}

/** O jogador do lado mais perto de um ponto do quadro do lado (atacando para a direita). */
function maisPerto(elenco: Elenco, ponto: Ponto, exceto: string[] = [], semGoleiro = true): string | null {
  let melhor: string | null = null;
  let menor = Infinity;
  elenco.ids.forEach((id, i) => {
    if (id === null || exceto.includes(id)) return;
    if (semGoleiro && elenco.slots[i] === "GOL") return;
    const b = elenco.bases[i] ?? MEIO;
    const d = Math.hypot(b.x - ponto.x, b.y - ponto.y);
    if (d < menor) {
      menor = d;
      melhor = id;
    }
  });
  return melhor;
}

/** Os que ficam mais à frente, para encher a área. */
function maisAdiantados(elenco: Elenco, quantos: number, exceto: string[]): string[] {
  return elenco.ids
    .map((id, i) => ({ id, x: elenco.bases[i]?.x ?? 0, pos: elenco.slots[i] }))
    .filter((e): e is { id: string; x: number; pos: string } => e.id !== null && e.pos !== "GOL" && !exceto.includes(e.id))
    .sort((a, b) => b.x - a.x)
    .slice(0, quantos)
    .map((e) => e.id);
}

/**
 * O roteiro de uma leva de lances.
 *
 * Os lances chegam em levas: um trecho de cada vez no solo, um trecho por
 * mensagem no online, a partida inteira de uma vez no "pular". A leva vira uma
 * fila de ações na ordem dos lances, e o estado (quem ficou com a bola, se os
 * lados já trocaram) atravessa para a leva seguinte.
 */
export function roteiroDosLances(partida: PartidaView, lances: LanceDaPartida[], estado: EstadoDoRoteiro): Acao[] {
  const acoes: Acao[] = [];
  const elencos: Record<Lado, Elenco> = {
    home: elencoDe("home", partida.home),
    away: elencoDe("away", partida.away),
  };
  const ladoDe = (clubId: string | null): Lado | null =>
    clubId === partida.home.clubId ? "home" : clubId === partida.away.clubId ? "away" : null;
  const ladoDoJogador = (id: string | null): Lado | null =>
    id === null ? null : elencos.home.ids.includes(id) ? "home" : elencos.away.ids.includes(id) ? "away" : null;

  let posse: Lado | null = null;
  /** Lances que outro lance já desenhou (a falta que virou cartão ou bola parada). */
  const consumidos = new Set<number>();

  for (let i = 0; i < lances.length; i += 1) {
    const lance = lances[i] as LanceDaPartida;
    if (consumidos.has(i)) continue;

    if (!estado.segundoTempo && segundoTempo(lance.minute)) {
      estado.segundoTempo = true;
      estado.dono = null;
      estado.bola = { ...MEIO };
      estado.saidaArmada = false;
      acoes.push({ tipo: "intervalo" });
    }

    const lado = ladoDe(lance.clubId);

    if (lance.kind === "posse") {
      if (!lado) continue;
      posse = lado;
      const proximo = lances
        .slice(i + 1)
        .find((l) => !["amarelo", "vermelho", "lesao", "substituicao", "falta", "impedimento"].includes(l.kind));
      const chanceDoTrecho = proximo && proximo.kind !== "posse" && proximo.kind !== "outro" ? proximo : null;
      acoes.push({ tipo: "lance", lance });
      acoes.push(...construcao(lance, lado, elencos, estado, chanceDoTrecho, ladoDoJogador));
      continue;
    }

    if (lance.kind === "falta") {
      // A falta que dá bola parada ou pênalti é desenhada pela própria chance.
      const seguinte = lances[i + 1];
      if (seguinte && CHANCES.has(seguinte.kind) && (seguinte.play === "bola_parada" || seguinte.play === "penalti")) continue;
      const cartao = seguinte && (seguinte.kind === "amarelo" || seguinte.kind === "vermelho") && seguinte.playerId === lance.playerId ? seguinte : null;
      if (cartao) consumidos.add(i + 1);
      acoes.push(...faltaComum(lance, cartao, elencos, estado, ladoDoJogador));
      continue;
    }

    if (lance.kind === "impedimento") {
      if (!lado) continue;
      acoes.push(...impedimento(lance, lado, elencos, estado, ladoDoJogador));
      continue;
    }

    if (lance.kind === "gol" || lance.kind === "chance" || lance.kind === "defesa" || lance.kind === "penalti_perdido") {
      if (lance.playerId === null && lance.kind === "gol") continue; // decisão por pênaltis
      const atacante = lance.kind === "defesa" ? (lado ? outroLado(lado) : posse) : lado;
      if (!atacante) continue;
      const defensor = outroLado(atacante);
      const chutador = lance.kind === "defesa" ? lance.shooterId : lance.playerId;
      const goleiro = lance.kind === "defesa" ? lance.playerId : goleiroDe(elencos[defensor]);
      const jogada = lance.play ?? "armacao";
      const anterior = lances[i - 1];
      const faltaAntes = anterior && anterior.kind === "falta" && (jogada === "bola_parada" || jogada === "penalti") ? anterior : null;
      acoes.push(...chance(lance, atacante, jogada, chutador, lance.assistId, goleiro, elencos, estado, faltaAntes));
      continue;
    }

    if (lance.kind === "amarelo" || lance.kind === "vermelho") {
      if (!lance.playerId) continue;
      acoes.push({ tipo: "corre", quem: lance.playerId, ate: "bola" });
      acoes.push({ tipo: "espera", s: 0.5 });
      acoes.push({ tipo: "lance", lance });
      acoes.push({ tipo: "marca", quem: lance.playerId, marca: lance.kind });
      acoes.push({ tipo: "espera", s: 0.7 });
      if (lance.kind === "vermelho") {
        acoes.push({ tipo: "sai", quem: lance.playerId });
        if (estado.dono === lance.playerId) estado.dono = null;
      }
      continue;
    }

    if (lance.kind === "lesao") {
      if (!lance.playerId) continue;
      acoes.push({ tipo: "lance", lance });
      acoes.push({ tipo: "marca", quem: lance.playerId, marca: "lesao" });
      acoes.push({ tipo: "espera", s: 0.8 });
      continue;
    }

    if (lance.kind === "substituicao") {
      if (!lance.playerId || !lance.assistId) continue;
      acoes.push({ tipo: "lance", lance });
      acoes.push({ tipo: "troca", sai: lance.assistId, entra: lance.playerId });
      if (estado.dono === lance.assistId) estado.dono = lance.playerId;
      acoes.push({ tipo: "espera", s: 0.5 });
      continue;
    }
  }

  return acoes;
}

const CHANCES: ReadonlySet<string> = new Set(["gol", "chance", "defesa", "penalti_perdido"]);

/**
 * A bola vai para o pé de alguém do lado dele: passe de quem a tem, ou
 * recolhida se está morta, ou desarme se está com o outro time.
 */
function bolaPara(
  acoes: Acao[],
  quem: string,
  lado: Lado,
  elencos: Record<Lado, Elenco>,
  estado: EstadoDoRoteiro,
  ladoDoJogador: (id: string | null) => Lado | null,
): void {
  if (estado.dono === quem) return;
  if (estado.dono && ladoDoJogador(estado.dono) === lado) {
    acoes.push({ tipo: "passe", de: estado.dono, para: quem, alto: false });
  } else if (estado.dono) {
    acoes.push({ tipo: "desarme", quem });
  } else {
    acoes.push({ tipo: "recolhe", quem });
  }
  estado.dono = quem;
  estado.bola = orientar(baseDe(elencos[lado], quem), ataqueParaDireita(lado, estado.segundoTempo ? 46 : 0));
}

/**
 * A falta comum: o infrator vai em quem tem a bola e derruba. O jogo para, o
 * cartão sai ali mesmo se houver, e a vítima levanta e cobra para um
 * companheiro. Quem sofreu a falta recebe a bola antes, se não a tinha.
 */
function faltaComum(
  lance: LanceDaPartida,
  cartao: LanceDaPartida | null,
  elencos: Record<Lado, Elenco>,
  estado: EstadoDoRoteiro,
  ladoDoJogador: (id: string | null) => Lado | null,
): Acao[] {
  const acoes: Acao[] = [];
  const quem = lance.playerId;
  const sobre = lance.assistId;
  const ladoDaVitima = ladoDoJogador(sobre);
  if (!quem || !sobre || !ladoDaVitima || ladoDoJogador(quem) === ladoDaVitima) return acoes;
  const el = elencos[ladoDaVitima];
  const paraDireita = ataqueParaDireita(ladoDaVitima, lance.minute);

  bolaPara(acoes, sobre, ladoDaVitima, elencos, estado, ladoDoJogador);
  acoes.push({ tipo: "conduz", quem: sobre, ate: null, avanco: 3, lateral: 0 });
  acoes.push({ tipo: "falta", quem, sobre });
  acoes.push({ tipo: "lance", lance });
  acoes.push({ tipo: "espera", s: 0.9 });
  if (cartao) {
    acoes.push({ tipo: "lance", lance: cartao });
    acoes.push({ tipo: "marca", quem, marca: cartao.kind === "vermelho" ? "vermelho" : "amarelo" });
    acoes.push({ tipo: "espera", s: 0.8 });
    if (cartao.kind === "vermelho") acoes.push({ tipo: "sai", quem });
  }

  // A cobrança: curta, para um companheiro da mesma linha ou da de trás.
  const linha = linhaDe(el, sobre);
  const candidatos = [...daLinha(el, linha, [sobre]), ...daLinha(el, Math.max(1, linha - 1), [sobre]), ...daLinha(el, Math.min(3, linha + 1), [sobre])];
  const para = candidatos[0] ?? null;
  acoes.push({ tipo: "recolhe", quem: sobre });
  if (para) {
    acoes.push({ tipo: "passe", de: sobre, para, alto: false });
    estado.dono = para;
    estado.bola = orientar(baseDe(el, para), paraDireita);
  } else {
    estado.dono = sobre;
    estado.bola = orientar(baseDe(el, sobre), paraDireita);
  }
  return acoes;
}

/**
 * O impedimento: um lançamento para o atacante que partiu cedo demais. A
 * bandeira sobe quando a bola chega nele, e a defesa cobra a falta de lá.
 */
function impedimento(
  lance: LanceDaPartida,
  lado: Lado,
  elencos: Record<Lado, Elenco>,
  estado: EstadoDoRoteiro,
  ladoDoJogador: (id: string | null) => Lado | null,
): Acao[] {
  const acoes: Acao[] = [];
  const quem = lance.playerId;
  const el = elencos[lado];
  const def = elencos[outroLado(lado)];
  if (!quem || !el.ids.includes(quem)) return acoes;
  const paraDireita = ataqueParaDireita(lado, lance.minute);
  const O = (p: Ponto): Ponto => orientar(p, paraDireita);

  // Quem lança: quem tem a bola, se é do lado; senão alguém do meio.
  let lancador = estado.dono && ladoDoJogador(estado.dono) === lado ? estado.dono : null;
  if (!lancador || lancador === quem) {
    lancador = daLinha(el, 2, [quem])[0] ?? daLinha(el, 1, [quem])[0] ?? null;
    if (!lancador) return acoes;
    bolaPara(acoes, lancador, lado, elencos, estado, ladoDoJogador);
  }
  const y = limitar(baseDe(el, quem).y, 12, 56);
  acoes.push({ tipo: "corre", quem, ate: O({ x: 89, y }) });
  acoes.push({ tipo: "conduz", quem: lancador, ate: null, avanco: 3, lateral: 0 });
  acoes.push({ tipo: "passe", de: lancador, para: quem, alto: true });
  acoes.push({ tipo: "impedimento", quem });
  acoes.push({ tipo: "lance", lance });
  acoes.push({ tipo: "espera", s: 0.9 });

  // A defesa cobra: o zagueiro mais perto recolhe e toca para o meio.
  const cobrador = maisPerto(def, espelhar({ x: 89, y }), []) ?? goleiroDe(def);
  if (!cobrador) {
    estado.dono = null;
    estado.bola = O({ x: 89, y });
    return acoes;
  }
  acoes.push({ tipo: "recolhe", quem: cobrador });
  const para = daLinha(def, 2, [cobrador])[0] ?? daLinha(def, 1, [cobrador])[0] ?? null;
  if (para) {
    acoes.push({ tipo: "passe", de: cobrador, para, alto: false });
    estado.dono = para;
    estado.bola = orientar(espelhar(baseDe(def, para)), paraDireita);
  } else {
    estado.dono = cobrador;
    estado.bola = orientar(espelhar(baseDe(def, cobrador)), paraDireita);
  }
  return acoes;
}

/**
 * O trecho com a bola: o time toma (ou recolhe) a bola e sobe com ela em
 * passes, linha a linha. Sem chance no trecho, termina com a bola no pé de
 * alguém; com chance, para onde a jogada começa.
 */
function construcao(
  lance: LanceDaPartida,
  lado: Lado,
  elencos: Record<Lado, Elenco>,
  estado: EstadoDoRoteiro,
  chanceDoTrecho: LanceDaPartida | null,
  ladoDoJogador: (id: string | null) => Lado | null,
): Acao[] {
  const acoes: Acao[] = [];
  const el = elencos[lado];
  const paraDireita = ataqueParaDireita(lado, lance.minute);
  const bolaNoQuadro = orientar(estado.bola, paraDireita);

  // 1. A bola: continua com alguém do lado, é tomada de alguém do outro, ou é
  // recolhida. Bola morta no meio é saída de jogo: dois no círculo central e
  // um passe curto entre eles, como todo jogo começa.
  let atual: string | null = null;
  let linhaDaSaida: number | null = null;
  const noMeio = estado.dono === null && Math.hypot(estado.bola.x - MEIO.x, estado.bola.y - MEIO.y) < 1;
  const [primeiro, segundo] = noMeio ? maisAdiantados(el, 2, []) : [];
  if (noMeio && primeiro && segundo) {
    const atras = orientar({ x: MEIO.x - 3, y: MEIO.y + 2 }, paraDireita);
    if (estado.saidaArmada) {
      acoes.push({ tipo: "posiciona", quem: primeiro, em: { ...MEIO } });
      acoes.push({ tipo: "posiciona", quem: segundo, em: atras });
    } else {
      acoes.push({ tipo: "corre", quem: segundo, ate: atras });
    }
    acoes.push({ tipo: "recolhe", quem: primeiro });
    acoes.push({ tipo: "passe", de: primeiro, para: segundo, alto: false });
    atual = segundo;
    // Quem recebe a saída devolve para o meio-campo, e dali o time sobe.
    linhaDaSaida = 1;
    estado.saidaArmada = false;
  } else if (estado.dono && ladoDoJogador(estado.dono) === lado) {
    atual = estado.dono;
  } else if (estado.dono) {
    atual = maisPerto(el, bolaNoQuadro);
    if (atual) acoes.push({ tipo: "desarme", quem: atual });
  } else {
    // Bola morta: perto do próprio gol é do goleiro; no resto, de quem está mais perto.
    atual = bolaNoQuadro.x < 14 ? goleiroDe(el) : maisPerto(el, bolaNoQuadro);
    if (atual) acoes.push({ tipo: "recolhe", quem: atual });
  }
  if (!atual) return acoes;

  // 2. Sobe em passes, linha a linha. O contra-ataque sai direto: um passe longo é a jogada.
  const jogada = chanceDoTrecho?.play ?? null;
  const passes = jogada === "contra_ataque" ? 1 : chanceDoTrecho ? 2 : 2 + Math.floor(hash(lance, 1) * 2);
  let linha = linhaDaSaida ?? linhaDe(el, atual);
  for (let n = 0; n < passes; n += 1) {
    const lateral = hash(lance, 10 + n) < 0.3 && linha >= 1;
    const proxima = lateral ? linha : Math.min(3, linha + 1);
    let candidatos = daLinha(el, proxima, [atual]);
    if (candidatos.length === 0) candidatos = daLinha(el, Math.min(3, proxima + 1), [atual]);
    if (candidatos.length === 0) candidatos = daLinha(el, 2, [atual]);
    if (candidatos.length === 0) break;
    // Prefere quem está mais perto na largura, para o passe não cruzar o campo inteiro.
    const y0 = baseDe(el, atual).y;
    candidatos.sort((a, b) => Math.abs(baseDe(el, a).y - y0) - Math.abs(baseDe(el, b).y - y0));
    const escolhido = Math.min(candidatos.length - 1, Math.floor(hash(lance, 20 + n) * Math.min(2, candidatos.length)));
    const para = candidatos[escolhido] as string;
    acoes.push({
      tipo: "conduz",
      quem: atual,
      ate: null,
      avanco: 3 + hash(lance, 30 + n) * 4,
      lateral: sinal(hash(lance, 40 + n)) * hash(lance, 50 + n) * 3,
    });
    const longe = Math.abs(baseDe(el, para).x - baseDe(el, atual).x) > 26 || jogada === "contra_ataque";
    acoes.push({ tipo: "passe", de: atual, para, alto: longe });
    atual = para;
    linha = proxima;
  }
  if (!chanceDoTrecho) acoes.push({ tipo: "conduz", quem: atual, ate: null, avanco: 4, lateral: sinal(hash(lance, 3)) * 2 });

  estado.dono = atual;
  estado.bola = orientar(baseDe(el, atual), paraDireita);
  return acoes;
}

/**
 * A jogada e o desfecho de uma chance.
 *
 * Tudo é desenhado atacando para a direita e virado no fim. O gol adversário
 * fica em x = 105, a marca do pênalti em 94, a grande área começa em 88,5.
 */
function chance(
  lance: LanceDaPartida,
  atacante: Lado,
  jogada: Jogada,
  chutador: string | null,
  assistente: string | null,
  goleiro: string | null,
  elencos: Record<Lado, Elenco>,
  estado: EstadoDoRoteiro,
  faltaAntes: LanceDaPartida | null = null,
): Acao[] {
  const acoes: Acao[] = [];
  const el = elencos[atacante];
  const def = elencos[outroLado(atacante)];
  const paraDireita = ataqueParaDireita(atacante, lance.minute);
  const O = (p: Ponto): Ponto => orientar(p, paraDireita);
  const s = sinal(hash(lance, 5));
  const excluir = [chutador, assistente].filter((id): id is string => id !== null);

  const atual0 = estado.dono && el.ids.includes(estado.dono) ? estado.dono : (maisPerto(el, { x: 60, y: MEIO.y }) ?? chutador);
  if (!atual0 || !chutador || !el.ids.includes(chutador)) return acoes;
  let atual: string = atual0;
  const passar = (para: string, alto = false): void => {
    if (atual === para) return;
    acoes.push({ tipo: "passe", de: atual, para, alto });
    atual = para;
  };
  const ladoDoCampo = (id: string | null): number => (baseDe(el, id).y < MEIO.y ? -1 : 1);
  const conduzAte = (quem: string, ate: Ponto): Acao => ({ tipo: "conduz", quem, ate: O(ate), avanco: 0, lateral: 0 });

  /** A falta que deu a bola parada: quem cobra leva a bola até o ponto e é derrubado ali. */
  const derrubado = (ponto: Ponto): void => {
    const infrator = faltaAntes?.playerId && def.ids.includes(faltaAntes.playerId) ? faltaAntes.playerId : null;
    if (!infrator || !faltaAntes) return;
    passar(chutador);
    acoes.push(conduzAte(chutador, ponto));
    acoes.push({ tipo: "falta", quem: infrator, sobre: chutador });
    acoes.push({ tipo: "lance", lance: faltaAntes });
    acoes.push({ tipo: "espera", s: 0.8 });
  };

  let alvoDoChute: Ponto;
  let chuteAlto = false;
  switch (jogada) {
    case "cruzamento": {
      const ponta = ladoDoCampo(assistente ?? chutador);
      const cruzador =
        assistente && el.ids.includes(assistente) ? assistente : (maisPerto(el, { x: 80, y: MEIO.y + ponta * 26 }, [chutador]) ?? atual);
      passar(cruzador);
      acoes.push(conduzAte(cruzador, { x: 94, y: MEIO.y + ponta * 27 }));
      acoes.push({ tipo: "corre", quem: chutador, ate: O({ x: 97, y: MEIO.y - ponta * 2.5 }) });
      maisAdiantados(el, 2, excluir).forEach((id, i) =>
        acoes.push({ tipo: "corre", quem: id, ate: O({ x: 94, y: MEIO.y + (i === 0 ? 7 : -7) }) }),
      );
      passar(chutador, true);
      alvoDoChute = { x: 106.4, y: MEIO.y + s * 2.4 };
      chuteAlto = true;
      break;
    }
    case "drible": {
      passar(chutador);
      const y0 = limitar(baseDe(el, chutador).y, 12, 56);
      acoes.push(conduzAte(chutador, { x: 72, y: y0 }));
      acoes.push(conduzAte(chutador, { x: 82, y: (y0 + MEIO.y) / 2 }));
      acoes.push(conduzAte(chutador, { x: 91, y: MEIO.y + s * 3 }));
      alvoDoChute = { x: 106.4, y: MEIO.y - s * 2.6 };
      break;
    }
    case "contra_ataque": {
      acoes.push({ tipo: "corre", quem: chutador, ate: O({ x: 74, y: MEIO.y + s * 6 }) });
      passar(chutador, true);
      acoes.push(conduzAte(chutador, { x: 89, y: MEIO.y + s * 2 }));
      alvoDoChute = { x: 106.4, y: MEIO.y - s * 2.2 };
      break;
    }
    case "bola_parada": {
      const bola = { x: 81, y: MEIO.y + s * 13 };
      derrubado({ x: bola.x - 1, y: bola.y });
      acoes.push({ tipo: "coloca", em: O(bola) });
      acoes.push({ tipo: "corre", quem: chutador, ate: O({ x: bola.x - 2.5, y: bola.y + s * 1.2 }) });
      acoes.push({ tipo: "recolhe", quem: chutador });
      maisAdiantados(el, 4, excluir).forEach((id, i) =>
        acoes.push({ tipo: "corre", quem: id, ate: O({ x: 96, y: MEIO.y + (i - 1.5) * 6 }) }),
      );
      maisAdiantados(def, 3, []).forEach((id, i) =>
        acoes.push({ tipo: "corre", quem: id, ate: O({ x: 90, y: bola.y - s * (i + 1) * 2.2 }) }),
      );
      acoes.push({ tipo: "espera", s: 1.2 });
      alvoDoChute = { x: 106.4, y: MEIO.y + s * 2.8 };
      chuteAlto = true;
      break;
    }
    case "penalti": {
      derrubado({ x: 95, y: MEIO.y + s * 4 });
      acoes.push({ tipo: "coloca", em: O({ x: 94, y: MEIO.y }) });
      acoes.push({ tipo: "corre", quem: chutador, ate: O({ x: 91, y: MEIO.y + 0.8 }) });
      acoes.push({ tipo: "recolhe", quem: chutador });
      if (goleiro) acoes.push({ tipo: "corre", quem: goleiro, ate: O({ x: 104.2, y: MEIO.y }) });
      const outros = [...maisAdiantados(el, 4, excluir), ...maisAdiantados(def, 4, [])];
      outros.forEach((id, i) =>
        acoes.push({ tipo: "corre", quem: id, ate: O({ x: 86 - (i % 2) * 2, y: 10 + (i / outros.length) * 48 }) }),
      );
      acoes.push({ tipo: "espera", s: 1.3 });
      alvoDoChute = { x: 106.4, y: MEIO.y + s * 2.6 };
      break;
    }
    default: {
      if (assistente && el.ids.includes(assistente)) {
        passar(assistente);
        acoes.push({ tipo: "conduz", quem: assistente, ate: null, avanco: 4, lateral: 0 });
      }
      passar(chutador);
      const y = limitar(baseDe(el, chutador).y, 16, 52);
      acoes.push(conduzAte(chutador, { x: 86, y: (y + MEIO.y) / 2 }));
      alvoDoChute = { x: 106.4, y: MEIO.y + s * 2.4 };
      break;
    }
  }

  // O desfecho: a bola vai para onde o motor disse que ela foi.
  let desfecho: Desfecho;
  if (lance.kind === "gol") desfecho = "gol";
  else if (lance.kind === "defesa") desfecho = "defesa";
  else if (lance.kind === "penalti_perdido") desfecho = hash(lance, 6) < 0.6 ? "defesa" : "fora";
  else desfecho = "fora";

  let alvo = alvoDoChute;
  if (desfecho === "defesa") alvo = { x: 103, y: MEIO.y + s * 3.2 };
  if (desfecho === "fora") alvo = { x: 108, y: MEIO.y + s * (jogada === "penalti" ? 6 : 15) };

  acoes.push({ tipo: "chute", quem: chutador, alvo: O(alvo), desfecho, goleiro, alto: chuteAlto || desfecho === "fora" });
  acoes.push({ tipo: "lance", lance });

  switch (desfecho) {
    case "gol": {
      acoes.push({ tipo: "marca", quem: chutador, marca: "gol" });
      acoes.push({ tipo: "espera", s: 1.6 });
      acoes.push({ tipo: "reinicio" });
      // Os dois mais adiantados de cada lado já vão para perto do meio: a saída
      // de jogo é de quem o motor disser no próximo trecho, e ele estará ali.
      for (const [quem, lado] of [
        [el, atacante],
        [def, outroLado(atacante)],
      ] as const) {
        const direita = ataqueParaDireita(lado, lance.minute);
        maisAdiantados(quem, 2, []).forEach((id, i) =>
          acoes.push({ tipo: "corre", quem: id, ate: orientar({ x: MEIO.x - 4 - i * 3, y: MEIO.y + (i === 0 ? -1.5 : 2.5) }, direita) }),
        );
      }
      estado.dono = null;
      estado.bola = { ...MEIO };
      break;
    }
    case "defesa": {
      // O goleiro fica com a bola e repõe para um defensor.
      acoes.push({ tipo: "espera", s: 0.6 });
      const reposicao = goleiro ? maisPerto(def, { x: 25, y: MEIO.y - s * 18 }, [goleiro]) : null;
      if (goleiro && reposicao) {
        acoes.push({ tipo: "passe", de: goleiro, para: reposicao, alto: true });
        estado.dono = reposicao;
        estado.bola = orientar(espelhar(baseDe(def, reposicao)), paraDireita);
      } else {
        estado.dono = goleiro;
        estado.bola = O({ x: 103, y: MEIO.y });
      }
      break;
    }
    default: {
      // Para fora: tiro de meta.
      acoes.push({ tipo: "espera", s: 0.4 });
      if (goleiro) {
        acoes.push({ tipo: "recolhe", quem: goleiro });
        acoes.push(conduzAte(goleiro, { x: 99.5, y: MEIO.y - s * 4 }));
        const reposicao = maisPerto(def, { x: 35, y: MEIO.y + s * 14 }, [goleiro]);
        if (reposicao) {
          acoes.push({ tipo: "passe", de: goleiro, para: reposicao, alto: true });
          estado.dono = reposicao;
          estado.bola = orientar(espelhar(baseDe(def, reposicao)), paraDireita);
        } else {
          estado.dono = goleiro;
          estado.bola = O({ x: 99.5, y: MEIO.y });
        }
      } else {
        estado.dono = null;
        estado.bola = O({ x: 99.5, y: MEIO.y });
      }
      break;
    }
  }

  return acoes;
}
